import { RefObject, useMemo } from 'react';

import { useElementHeight } from '@cast/utils';

export const useAvailableHeight = (
  rootRef: RefObject<HTMLElement | null>,
  headerRef: RefObject<HTMLElement | null>,
  enabled = true
) => {
  const parentHeight = useElementHeight(
    rootRef.current?.parentElement,
    enabled
  );
  const headerHeight = useElementHeight(headerRef.current, enabled);

  return useMemo(() => {
    if (!enabled || parentHeight === null || headerHeight === null) {
      return null;
    }

    const root = rootRef.current;
    const parent = root?.parentElement;
    if (!parent) {
      return null;
    }

    // Collapse root so it doesn't prevent parent from shrinking
    const prevHeight = root.style.height;
    const prevOverflow = root.style.overflow;
    root.style.height = '0px';
    root.style.overflow = 'hidden';

    const children = Array.from(parent.children);
    const siblings = children.filter((child) => child !== root);
    const siblingsHeight = siblings.reduce(
      (sum, child) => sum + child.getBoundingClientRect().height,
      0
    );
    const gap = parseFloat(getComputedStyle(parent).gap) || 0;
    const gapTotal = gap * (children.length - 1);
    const available = Math.round(
      parent.getBoundingClientRect().height -
        siblingsHeight -
        gapTotal -
        headerHeight
    );

    // Restore
    root.style.height = prevHeight;
    root.style.overflow = prevOverflow;

    return available;
  }, [enabled, parentHeight, headerHeight, rootRef]);
};
